import { Menu, MenuItem } from "@szhsin/react-menu";
import { compact } from "lodash";
import { useCallback, useMemo } from "react";
import { TrendReportGroupBy, TrendReportType } from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import useLabel, { UseLabelType } from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import RecipientDropdown from "@components/people-dropdown/recipient-dropdown";
import Tooltip from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import {
  TopicflowIconMap,
  getTopicflowIcon,
} from "@components/topicflow-icon/topicflow-icon-helpers";
import UserCombobox from "@components/user-combobox/user-combobox";
import { classNames } from "@helpers/css";

import { TeamPickerDropdown, TeamPickerTeam } from "../../team-picker";
import { TrendReportFilterItem, TrendReportFilterType } from "./types";

const filterTypeToTitle = (
  label: UseLabelType,
): { [key in TrendReportFilterType]: string } => ({
  [TrendReportFilterType.TEAM]: label("team", {
    capitalize: true,
    pluralize: true,
  }),
  [TrendReportFilterType.MANAGER]: "Managers",
});

const Filter = ({
  filter,
  onChange,
  onRemove,
}: {
  filter: TrendReportFilterItem;
  onChange: (filterItems: TeamPickerTeam[] | BasicUser[]) => void;
  onRemove: () => void;
}) => {
  const label = useLabel();

  const addButton = useMemo(() => {
    switch (filter.type) {
      case TrendReportFilterType.TEAM:
        return (
          <TeamPickerDropdown
            teams={filter.data}
            onChange={(team) => onChange([...filter.data, team])}
          >
            <ComboboxGeneric.Button
              icon={getTopicflowIcon("plus")}
              theme={buttonTheme.iconGray}
            />
          </TeamPickerDropdown>
        );
      case TrendReportFilterType.MANAGER:
        return (
          <RecipientDropdown
            showCurrentUser
            recipients={filter.data}
            onChangeRecipient={(user) => onChange([...filter.data, user])}
          >
            <UserCombobox.Button
              icon={getTopicflowIcon("plus")}
              theme={buttonTheme.iconGray}
            />
          </RecipientDropdown>
        );
      default:
        return null;
    }
  }, [filter, onChange]);

  const handleRemoveItem = useCallback(
    (id: number) => {
      switch (filter.type) {
        case TrendReportFilterType.TEAM:
          onChange(filter.data.filter((team) => team.id !== id));
          return;
        case TrendReportFilterType.MANAGER:
          onChange(filter.data.filter((manager) => manager.id !== id));
          return;
      }
    },
    [filter, onChange],
  );

  const dataItemsContent = useMemo(() => {
    let dataItems: { label: string; id: number }[] = [];
    switch (filter.type) {
      case TrendReportFilterType.TEAM:
        dataItems = filter.data.map((team) => ({
          label: team.title,
          id: team.id,
        }));
        break;
      case TrendReportFilterType.MANAGER:
        dataItems = filter.data.map((manager) => ({
          label: manager.name,
          id: manager.id,
        }));
        break;
    }

    return dataItems.length === 0 ? (
      <div className="mt-2 text-gray-400">No items added</div>
    ) : (
      <div className="mt-2 flex items-center gap-2">
        {dataItems.map((item) => (
          <div
            className="border rounded-lg px-2 py-1 text-sm flex items-center gap-1"
            key={item.label}
          >
            {item.label}
            <button
              className="ml-0.5 rounded-full p-0.5 border bg-gray-50 text-gray-600 hover:text-white hover:bg-blue-600 hover:border-blue-600"
              onClick={() => handleRemoveItem(item.id)}
            >
              <TopicflowIcon type="close" className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    );
  }, [filter, handleRemoveItem]);

  return (
    <div className="p-4 border rounded-md">
      <div className="flex items-center justify-between">
        <div className="font-medium">
          {filterTypeToTitle(label)[filter.type]}
        </div>
        <div className="flex items-center gap-2">
          {addButton}
          <Button
            className="text-gray-600"
            icon={TopicflowIconMap.trash}
            theme={buttonTheme.iconGray}
            onClick={onRemove}
          />
        </div>
      </div>
      {dataItemsContent}
    </div>
  );
};

type FilterSelectOption = {
  label: string;
  type: TrendReportFilterType;
  disabled?: boolean;
  tooltip?: string;
};

const FilterMenuItem = ({
  option,
  filters,
  onUpdateFilters,
}: {
  option: FilterSelectOption;
  filters: TrendReportFilterItem[];
  onUpdateFilters: (filterData: TrendReportFilterItem[]) => void;
}) => {
  return (
    <MenuItem
      className={classNames(
        "text-sm block",
        option.disabled && "text-gray-300",
      )}
      onClick={() => {
        if (option.disabled) {
          return;
        }
        const newFilter: TrendReportFilterItem = {
          type: option.type,
          data: [],
        };
        onUpdateFilters([...filters, newFilter]);
      }}
    >
      <Tooltip text={option.tooltip}>
        <div>{option.label}</div>
      </Tooltip>
    </MenuItem>
  );
};

const hasFilterType = (
  filters: TrendReportFilterItem[],
  type: TrendReportFilterType,
): boolean => {
  return filters.some((filter) => filter.type === type);
};

const updateFilters = (
  existingFilters: TrendReportFilterItem[],
  filterType: TrendReportFilterType,
  data: TeamPickerTeam[] | BasicUser[],
): TrendReportFilterItem[] => {
  if (hasFilterType(existingFilters, filterType)) {
    return existingFilters.map((filter) => {
      if (filter.type === filterType) {
        return {
          type: filterType,
          data,
        } as TrendReportFilterItem;
      }
      return filter;
    });
  }

  return [
    ...existingFilters,
    { type: filterType, data } as TrendReportFilterItem,
  ];
};

const TrendReportFilterContainer = ({
  trendReportType,
  trendReportGroupBy,
  filters,
  onUpdateFilters,
}: {
  trendReportType: TrendReportType;
  trendReportGroupBy: TrendReportGroupBy;
  filters: TrendReportFilterItem[];
  onUpdateFilters: (filterData: TrendReportFilterItem[]) => void;
}) => {
  const label = useLabel();

  const addFilterOptions: FilterSelectOption[] = useMemo(
    () =>
      compact([
        !hasFilterType(filters, TrendReportFilterType.MANAGER) && {
          label: filterTypeToTitle(label)[TrendReportFilterType.MANAGER],
          type: TrendReportFilterType.MANAGER,
          disabled:
            trendReportType === TrendReportType.Average &&
            trendReportGroupBy === TrendReportGroupBy.Team,
          tooltip:
            trendReportType === TrendReportType.Average &&
            trendReportGroupBy === TrendReportGroupBy.Team
              ? "Only allowed when grouping by Manager"
              : undefined,
        },
        !hasFilterType(filters, TrendReportFilterType.TEAM) && {
          label: filterTypeToTitle(label)[TrendReportFilterType.TEAM],
          type: TrendReportFilterType.TEAM,
          disabled:
            trendReportType === TrendReportType.Average &&
            trendReportGroupBy === TrendReportGroupBy.Manager,
          tooltip:
            trendReportType === TrendReportType.Average &&
            trendReportGroupBy === TrendReportGroupBy.Manager
              ? `Only allowed when grouping by ${label("team", {
                  capitalize: true,
                })}`
              : undefined,
        },
      ]),
    [filters, label, trendReportGroupBy, trendReportType],
  );

  const disabled = addFilterOptions.length === 0;
  return (
    <div className="flex flex-col gap-4">
      {filters.map((item) => {
        return (
          <Filter
            key={item.type}
            filter={item}
            onChange={(data) =>
              onUpdateFilters(updateFilters(filters, item.type, data))
            }
            onRemove={() =>
              onUpdateFilters(filters.filter((f) => f.type !== item.type))
            }
          />
        );
      })}

      <Menu
        className="text-sm z-dropdown"
        portal
        transition={false}
        menuButton={
          <button
            disabled={disabled}
            className={classNames(
              "w-fit -ml-2 px-2 py-1 text-left text-sm font-medium rounded flex items-center gap-1.5 text-gray-600 hover:bg-gray-100",
              disabled && "opacity-50",
            )}
          >
            <TopicflowIcon type="plus" className="h-4 w-4" />
            Add Filter
          </button>
        }
      >
        {addFilterOptions.map((option) => {
          return (
            <FilterMenuItem
              key={option.label}
              option={option}
              filters={filters}
              onUpdateFilters={onUpdateFilters}
            />
          );
        })}
      </Menu>
    </div>
  );
};

export default TrendReportFilterContainer;
